/**
 * Occupation Service
 * API integration for occupation CRUD operations
 */

import apiClient from './apiClient';
import { PaginatedResponse } from '../types/common';

// Special Activity Types Enum (matching backend)
export enum SpecialActivityType {
  HEIGHT_WORK = 'height_work',
  CONFINED_SPACE = 'confined_space',
  ELECTRICAL = 'electrical',
  FORKLIFT = 'forklift',
  FOOD_HANDLING = 'food_handling',
  PEMT = 'pemt',
  HOT_WORK = 'hot_work',
  SELF_PROPELLED_MACHINE = 'self_propelled_machine',
}

// Special Activity Display Configuration
export interface SpecialActivityConfig {
  value: SpecialActivityType;
  label: string;
  icon: string;
  color: string;
}

export const SPECIAL_ACTIVITIES: SpecialActivityConfig[] = [
  {
    value: SpecialActivityType.HEIGHT_WORK,
    label: 'Trabalho em Altura',
    icon: '⬆️',
    color: 'warning'
  },
  {
    value: SpecialActivityType.CONFINED_SPACE,
    label: 'Espaço Confinado',
    icon: '🔒',
    color: 'error'
  },
  {
    value: SpecialActivityType.ELECTRICAL,
    label: 'Trabalho Elétrico',
    icon: '⚡',
    color: 'warning'
  },
  {
    value: SpecialActivityType.FORKLIFT,
    label: 'Empilhadeira',
    icon: '🚜',
    color: 'info'
  },
  {
    value: SpecialActivityType.FOOD_HANDLING,
    label: 'Manipulação de Alimentos',
    icon: '🍽️',
    color: 'success'
  },
  {
    value: SpecialActivityType.PEMT,
    label: 'PEMT - Plataforma Elevatória',
    icon: '🎧',
    color: 'info'
  },
  {
    value: SpecialActivityType.HOT_WORK,
    label: 'Trabalho a Quente',
    icon: '🔥',
    color: 'error'
  },
  {
    value: SpecialActivityType.SELF_PROPELLED_MACHINE,
    label: 'Máquina Autopropelida',
    icon: '🚛',
    color: 'info'
  }
];

// Occupation Special Activity (from backend)
export interface OccupationSpecialActivity {
  id: number;
  occupationId: number;
  activity: SpecialActivityType;
  createdAt?: string;
  updatedAt?: string;
}

// Types
export interface Occupation {
  id: number;
  name: string;
  cboCode?: string;
  description?: string;
  role?: string;
  departmentId: number;
  companyId: number;
  specialActivities?: OccupationSpecialActivity[];
  department?: {
    id: number;
    name: string;
  };
  company?: {
    id: number;
    tradeName?: string;
    legalName: string;
  };
  // Alert counts (optional - returned when includeCounts=true)
  _counts?: {
    employees?: number;
    riskApplications?: number;
    sourceApplications?: number;
    examApplications?: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateOccupationRequest {
  name: string;
  cboCode?: string;
  description?: string;
  role?: string;
  departmentId: number;
  companyId: number;
  specialActivities?: SpecialActivityType[];
}

export interface UpdateOccupationRequest {
  name?: string;
  cboCode?: string;
  description?: string;
  role?: string;
  departmentId?: number;
  companyId?: number;
  specialActivities?: SpecialActivityType[];
}

export interface OccupationFilters {
  companyId?: number;
  departmentId?: number;
  search?: string;
  specialActivities?: SpecialActivityType[];
}

export interface OccupationListParams {
  page?: number;
  limit?: number;
  search?: string;
  filters?: Partial<OccupationFilters>;
}

class OccupationService {
  private baseUrl = '/occupations';

  /**
   * Get paginated list of occupations
   */
  async getOccupations(params: OccupationListParams = {}): Promise<PaginatedResponse<Occupation>> {
    const { page = 1, limit = 10, search, filters } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      queryParams.append('search', search);
    }

    if (filters) {
      if (filters.companyId) {
        queryParams.append('companyId', filters.companyId.toString());
      }
      if (filters.departmentId) {
        queryParams.append('departmentId', filters.departmentId.toString());
      }
      
      // Special Activities filters - send as array
      if (filters.specialActivities && filters.specialActivities.length > 0) {
        queryParams.append('specialActivities', filters.specialActivities.join(','));
      }
    }

    const response = await apiClient.get(`${this.baseUrl}?${queryParams}`);
    
    // httpClient já retorna response.data, então response é { data: Occupation[], pagination: {...} }
    // Precisamos acessar response.data e response.pagination (não response.data.data)
    const apiResponse = response as unknown as { data: Occupation[]; pagination: { total: number; page: number; limit: number; totalPages: number } };
    return {
      data: apiResponse.data ?? [],
      pagination: {
        total: apiResponse.pagination?.total || 0,
        page: apiResponse.pagination?.page || page,
        limit: apiResponse.pagination?.limit || limit,
        totalPages: apiResponse.pagination?.totalPages || 0
      }
    } as PaginatedResponse<Occupation>;
  }

  /**
   * Get occupation by ID
   */
  async getOccupationById(id: number): Promise<Occupation> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create new occupation
   */
  async createOccupation(data: CreateOccupationRequest): Promise<Occupation> {
    console.log('💼 Creating occupation:', data);
    
    const response = await apiClient.post(this.baseUrl, data);
    
    console.log('✅ Occupation created successfully:', response);
    return response.data;
  }

  /**
   * Update existing occupation
   */
  async updateOccupation(id: number, data: UpdateOccupationRequest): Promise<Occupation> {
    console.log('💼 Updating occupation:', { id, data });
    
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    
    console.log('✅ Occupation updated successfully:', response);
    return response.data;
  }

  /**
   * Delete occupation
   */
  async deleteOccupation(id: number): Promise<void> {
    console.log('🗑️ Deleting occupation:', id);
    
    await apiClient.delete(`${this.baseUrl}/${id}`);
    
    console.log('✅ Occupation deleted successfully');
  }

  /**
   * Check if occupation has dependencies (employees)
   */
  async checkDependencies(id: number): Promise<{
    hasEmployees: boolean;
    employeeCount: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data;
  }

  /**
   * Export occupations to Excel
   */
  async exportOccupations(filters?: Partial<OccupationFilters>): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const response = await apiClient.get(`${this.baseUrl}/export?${queryParams}`, {
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  /**
   * Get occupation statistics
   */
  async getOccupationStats(): Promise<{
    total: number;
    byCompany: Record<string, number>;
    byDepartment: Record<string, number>;
    withSpecialActivities: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data;
  }
}

export const occupationService = new OccupationService();
export default occupationService;
